import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Article } from '../types';
import { Loader, ArrowLeft } from 'lucide-react';

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setArticle({ id: docSnap.id, ...docSnap.data() } as Article);
        } else {
          console.log("No such article!");
        }
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen pt-32 flex justify-center">
        <Loader className="animate-spin" />
    </div>
  );

  if (!article) return (
    <div className="min-h-screen pt-32 text-center px-4">
        <h2 className="font-heading text-2xl font-bold mb-4">Article Not Found</h2>
        <button onClick={() => navigate('/')} className="text-sm border-b border-black pb-1 hover:opacity-50">Return Home</button>
    </div>
  );

  return (
    <article className="min-h-screen pt-24 pb-20">
      {/* Hero */}
      <div className="w-full h-[50vh] md:h-[70vh] relative mb-12">
        <img
          src={article.heroImage || '/assets/placeholder.svg'}
          alt={article.title}
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
          }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-12 bg-gradient-to-t from-black/80 to-transparent text-white">
            <div className="max-w-4xl mx-auto">
                <span className="inline-block px-3 py-1 bg-white text-black text-xs font-bold uppercase tracking-widest mb-4">
                    {article.type}
                </span>
                <h1 className="font-heading text-4xl md:text-6xl font-bold leading-tight mb-4">
                    {article.title}
                </h1>
                {article.subtitle && (
                    <p className="font-body text-lg md:text-xl text-gray-200 max-w-2xl">
                        {article.subtitle}
                    </p>
                )}
                <div className="mt-6 flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-gray-300">
                    <span>{new Date(article.publishDate).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>By {article.author}</span>
                </div>
            </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 font-body leading-relaxed text-lg text-gray-800 whitespace-pre-wrap">
        {article.content}
      </div>

      {/* Footer Navigation */}
      <div className="max-w-3xl mx-auto px-4 mt-16 pt-8 border-t border-gray-200">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:text-gray-500 transition-colors">
            <ArrowLeft size={16} /> Back
        </button>
      </div>
    </article>
  );
};

export default ArticleDetail;
